import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import pushNotificationService from '../services/pushNotificationService';

const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const user = useSelector((state) => state.User);

  useEffect(() => {
    const initializePushNotifications = async () => {
      try {
        setIsLoading(true);
        const supported = await pushNotificationService.initialize();
        setIsSupported(supported);
        
        if (supported) {
          const subscribed = await pushNotificationService.isSubscribed();
          setIsSubscribed(subscribed);
          
          // Log current permission status for debugging
          console.log('Notification permission:', Notification.permission);
          console.log('Is subscribed:', subscribed);
        }
      } catch (error) {
        console.error('Error initializing push notifications:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (user.uuid) {
      initializePushNotifications();
    }
  }, [user.uuid]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !user.uuid) {
      throw new Error('Push notifications not supported or user not logged in');
    }

    // Check if VAPID key is configured
    if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
      const errorMsg = 'VAPID public key not configured. Please check your environment variables.';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      setIsLoading(true);
      setError(null);
      
      await pushNotificationService.requestPermission();
      await pushNotificationService.subscribe(user.uuid);
      setIsSubscribed(true);
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user.uuid]);

  const unsubscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await pushNotificationService.unsubscribe();
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const showNotification = useCallback(async (title, options = {}) => {
    try {
      await pushNotificationService.showNotification(title, options);
    } catch (error) {
      console.error('Error showing notification:', error);
      setError(error.message);
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    showNotification
  };
};

export default usePushNotifications;
